import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle2, Clock, XCircle, AlertCircle } from 'lucide-react';

interface Vendor {
  id: string;
  name: string;
  category: string;
  vendor_category?: string;
}

interface VendorTrainingProgress {
  vendor_id: string;
  status: 'not_started' | 'uploading' | 'processing' | 'mapping' | 'reviewing' | 'completed';
  invoices_uploaded: number;
  invoices_processed: number;
  clusters_identified: number;
  clusters_mapped: number;
}

interface COGSVendorTrainingStep1Props {
  onNext: () => void;
  onVendorsSelected: (vendorIds: string[]) => void;
}

export const COGSVendorTrainingStep1 = ({ onNext, onVendorsSelected }: COGSVendorTrainingStep1Props) => {
  const { toast } = useToast();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendors, setSelectedVendors] = useState<string[]>([]);
  const [trainingProgress, setTrainingProgress] = useState<Record<string, VendorTrainingProgress>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCOGSVendors();
  }, []);

  useEffect(() => {
    if (vendors.length > 0) {
      loadTrainingProgress();
    }
  }, [vendors]);

  const loadCOGSVendors = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      // Load ALL vendors, not just COGS
      const { data, error } = await supabase
        .from('vendors')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;

      setVendors((data || []) as Vendor[]);
      // Auto-select only COGS vendors by default (these require training)
      const cogsVendorIds = (data || []).filter(v => v.category === 'COGS').map(v => v.id);
      setSelectedVendors(cogsVendorIds);
      onVendorsSelected(cogsVendorIds);
      // Initialize progress for auto-selected vendors
      if (cogsVendorIds.length > 0) {
        await initializeProgress(cogsVendorIds);
      }
    } catch (error: any) {
      console.error('Error loading vendors:', error);
      toast({
        title: 'Error',
        description: 'Failed to load vendors',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadTrainingProgress = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId || vendors.length === 0) return;

      const vendorIds = vendors.map(v => v.id);
      const { data, error } = await supabase
        .from('vendor_training_progress')
        .select('*')
        .eq('company_id', companyId)
        .in('vendor_id', vendorIds);

      if (error) throw error;

      const progressMap: Record<string, VendorTrainingProgress> = {};
      (data || []).forEach((progress: any) => {
        progressMap[progress.vendor_id] = {
          vendor_id: progress.vendor_id,
          status: progress.status || 'not_started',
          invoices_uploaded: progress.invoices_uploaded || 0,
          invoices_processed: progress.invoices_processed || 0,
          clusters_identified: progress.clusters_identified || 0,
          clusters_mapped: progress.clusters_mapped || 0,
        };
      });

      setTrainingProgress(progressMap);
    } catch (error: any) {
      console.error('Error loading training progress:', error);
    }
  };

  const initializeProgress = async (vendorIds: string[]) => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      // Upsert progress records for selected vendors
      const progressRecords = vendorIds.map(vendorId => ({
        company_id: companyId,
        vendor_id: vendorId,
        status: 'not_started' as const,
        invoices_uploaded: 0,
        invoices_processed: 0,
        clusters_identified: 0,
        clusters_mapped: 0,
        ingredients_created: 0,
      }));

      const { error } = await supabase
        .from('vendor_training_progress')
        .upsert(progressRecords, {
          onConflict: 'company_id,vendor_id',
        });

      if (error) throw error;

      // Reload progress to get updated data
      await loadTrainingProgress();
    } catch (error: any) {
      console.error('Error initializing progress:', error);
      toast({
        title: 'Error',
        description: 'Failed to initialize training progress',
        variant: 'destructive',
      });
    }
  };

  const toggleVendor = async (vendorId: string) => {
    const newSelected = selectedVendors.includes(vendorId)
      ? selectedVendors.filter(id => id !== vendorId)
      : [...selectedVendors, vendorId];
    
    setSelectedVendors(newSelected);
    onVendorsSelected(newSelected);
    
    // Initialize progress for newly selected vendors
    if (!selectedVendors.includes(vendorId)) {
      await initializeProgress([vendorId]);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500"><CheckCircle2 className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'reviewing':
        return <Badge className="bg-blue-500"><Clock className="h-3 w-3 mr-1" />Reviewing</Badge>;
      case 'mapping':
        return <Badge className="bg-purple-500"><Clock className="h-3 w-3 mr-1" />Mapping</Badge>;
      case 'processing':
        return <Badge className="bg-yellow-500"><Clock className="h-3 w-3 mr-1" />Processing</Badge>;
      case 'uploading':
        return <Badge className="bg-orange-500"><Clock className="h-3 w-3 mr-1" />Uploading</Badge>;
      case 'not_started':
      default:
        return <Badge variant="outline"><AlertCircle className="h-3 w-3 mr-1" />Not Started</Badge>;
    }
  };

  const handleSelectAll = async (checked: boolean) => {
    if (checked) {
      // Only select COGS vendors (those that require training)
      const cogsVendorIds = vendors.filter(v => v.category === 'COGS').map(v => v.id);
      setSelectedVendors(cogsVendorIds);
      onVendorsSelected(cogsVendorIds);
      await initializeProgress(cogsVendorIds);
    } else {
      setSelectedVendors([]);
      onVendorsSelected([]);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-48" />
                  </div>
                </div>
                <Skeleton className="h-9 w-24" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (vendors.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground mb-4">
          No vendors found. Please add vendors in Phase 1 first.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Select Vendors for Invoice Training</h3>
        <p className="text-sm text-muted-foreground">
          Only COGS vendors require invoice training. Other vendors will be matched from bank transactions. Select which COGS vendors you want to train on invoice recognition.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="mb-4">
            <p className="text-sm font-medium">
              {selectedVendors.length} of {vendors.filter(v => v.category === 'COGS').length} COGS vendors selected
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {vendors.filter(v => v.category !== 'COGS').length} non-COGS vendor(s) will use bank transaction matching only
            </p>
          </div>
          <div className="space-y-2">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">
                    <Checkbox
                      checked={selectedVendors.length === vendors.filter(v => v.category === 'COGS').length && vendors.filter(v => v.category === 'COGS').length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </th>
                  <th className="text-left p-2">Vendor Name</th>
                  <th className="text-left p-2">Vendor Type</th>
                  <th className="text-left p-2">Training Required</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Invoices</th>
                </tr>
              </thead>
              <tbody>
                {vendors.map((vendor) => {
                  const progress = trainingProgress[vendor.id];
                  const invoiceCount = progress?.invoices_uploaded || 0;
                  const status = progress?.status || 'not_started';
                  const isCOGS = vendor.category === 'COGS';
                  const requiresTraining = isCOGS;
                  
                  return (
                    <tr key={vendor.id} className="border-b">
                      <td className="p-2">
                        <Checkbox
                          checked={selectedVendors.includes(vendor.id)}
                          onCheckedChange={() => toggleVendor(vendor.id)}
                          disabled={!requiresTraining}
                        />
                      </td>
                      <td className="p-2 font-medium">{vendor.name}</td>
                      <td className="p-2">
                        <Badge variant="outline">
                          {vendor.vendor_category || vendor.category || 'Other'}
                        </Badge>
                      </td>
                      <td className="p-2">
                        {requiresTraining ? (
                          <Badge className="bg-blue-500">Invoice Training Required</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-gray-100">Bank Matching Only</Badge>
                        )}
                      </td>
                      <td className="p-2">
                        {requiresTraining ? getStatusBadge(status) : <Badge variant="outline">N/A</Badge>}
                      </td>
                      <td className="p-2 text-sm">
                        {requiresTraining ? (
                          <span className={invoiceCount >= 5 ? 'text-green-600 font-medium' : 'text-muted-foreground'}>
                            {invoiceCount}/5
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

